import React from 'react';
import { FormProvider } from 'react-hook-form';
import _ from 'lodash';
// mui
import { Card as MuiCard, Grid, FormHelperText } from '@mui/material';
import { withStyles } from '@mui/styles';

import ToggleButton from '@mui/lab/ToggleButton';
// components
import { TextAreaBase } from '../../../components/inputs/TextAreaBase';
import { withFormController } from '../../../components/hocs/forms';
import { InputLabelBase } from '../../../components/inputs/InputLabelBase';
import { withShowable } from '../lib/withShowable';
import { ControlledToggleButtonGroup } from '../../../components/Buttons/ControlledToggleButtonGroup';

const TextArea = withFormController(TextAreaBase);
const QuestionBox = withShowable(Grid);

const RenewalQuestions = ({
  classes,
  isWithinExpiryPeriod,
  readOnly = false,
  cardStyle,
  methods,
}: any) => {
  const toggleButtonProps = {
    disabled: readOnly,
    className: classes.toggleButton,
    classes: {
      disabled: classes.toggleButtonDisabled,
    },
  };

  const LabelProps = {
    required: true,
    TypographyProps: {
      variant: 'body2',
      gutterBottom: true,
    },
  };
  const [
    incidentResponsePlan,
    backupFrequency,
    backupType,
    mfaAuthentication,
    mfaType,
    changeInScope,
    backupFrequencyOther,
    mfaOther,
    changeInScopeDetail,
  ] = methods.watch([
    'incidentResponsePlan',
    'backupFrequency',
    'backupType',
    'mfaAuthentication',
    'mfaType',
    'changeInScope',
    'backupFrequencyOther',
    'mfaOther',
    'changeInScopeDetail',
  ]);

  const values = methods.getValues();
  const cardCss =
    typeof cardStyle === 'function' ? cardStyle().securityStyles : null;

  return (
    <FormProvider {...methods}>
      <MuiCard className={cardCss}>
        <Grid item md={12}>
          <QuestionBox
            container
            justifyContent="left"
            className={classes.inputFields}
            style={{ paddingTop: 0 }}
            show={!values.renewalChangeInAnswers}
          >
            <Grid container style={{ paddingTop: 0 }}>
              <Grid item md={12}>
                <div>
                  <InputLabelBase
                    {...LabelProps}
                    className={classes.greenLabel}
                  >
                    How often does the organization perform backups of
                    business-critical data?
                  </InputLabelBase>
                  <ControlledToggleButtonGroup name="backupFrequency" exclusive>
                    <ToggleButton value="WEEKLY" {...toggleButtonProps}>
                      <div>Weekly or more</div>
                    </ToggleButton>
                    <ToggleButton value="MONTHLY" {...toggleButtonProps}>
                      <div>Monthly</div>
                    </ToggleButton>
                    <ToggleButton value="QUARTERLY" {...toggleButtonProps}>
                      <div>Quarterly</div>
                    </ToggleButton>
                    <ToggleButton value="SIX_MONTHS" {...toggleButtonProps}>
                      <div>Every 6 Months</div>
                    </ToggleButton>
                    <ToggleButton value="NEVER" {...toggleButtonProps}>
                      <div>Never</div>
                    </ToggleButton>
                  </ControlledToggleButtonGroup>
                  <FormHelperText className="api-text-error">
                    {_.get(
                      methods,
                      'formState.errors.backupFrequency.message',
                      ''
                    )}
                  </FormHelperText>

                  <QuestionBox
                    show={
                      backupFrequency !== 'NEVER' &&
                      typeof backupFrequency === 'string'
                    }
                  >
                    <div>
                      <InputLabelBase {...LabelProps} style={{ padding: 0 }}>
                        <span style={{ fontStyle: 'italic' }}>
                          (if your organization performs backups, please select
                          all that apply)
                        </span>
                        <br />
                        Backups are:
                      </InputLabelBase>
                      <ControlledToggleButtonGroup name="backupType">
                        <ToggleButton
                          value="backupFrequencyEncrypted"
                          {...toggleButtonProps}
                        >
                          <div>Encrypted</div>
                        </ToggleButton>
                        <ToggleButton
                          value="backupFrequencyTested"
                          {...toggleButtonProps}
                        >
                          <div>Tested</div>
                        </ToggleButton>
                        <ToggleButton
                          value="backupFrequencySeparate"
                          {...toggleButtonProps}
                        >
                          <div>Online or Designated Cloud Service</div>
                        </ToggleButton>
                        <ToggleButton
                          value="backupFrequencyOther"
                          {...toggleButtonProps}
                        >
                          <div>Other</div>
                        </ToggleButton>
                      </ControlledToggleButtonGroup>
                      <FormHelperText className="api-text-error">
                        {_.get(
                          methods,
                          'formState.errors.backupType.message',
                          ''
                        )}
                      </FormHelperText>
                    </div>
                  </QuestionBox>

                  <QuestionBox
                    show={
                      backupFrequency !== 'NEVER' &&
                      typeof backupType === 'object' &&
                      backupType.includes('backupFrequencyOther')
                    }
                  >
                    <div>
                      <InputLabelBase
                        {...LabelProps}
                        style={{ fontStyle: 'italic', padding: 0 }}
                      >
                        (Other: Please specify)
                      </InputLabelBase>
                      <TextArea
                        multiline
                        minRows={4}
                        name="backupFrequencyOther"
                        fullWidth
                        className={classes.input}
                      />
                    </div>
                    <FormHelperText className="api-text-error">
                      {_.get(
                        methods,
                        'formState.errors.backupFrequencyOther.message',
                        ''
                      )}
                    </FormHelperText>
                  </QuestionBox>
                </div>
              </Grid>
            </Grid>

            <Grid item md={12}>
              <div>
                <InputLabelBase {...LabelProps}>
                  Do you enforce Multi-Factor Authentication (MFA) for all
                  employees, contractors, and partners?
                </InputLabelBase>

                <ControlledToggleButtonGroup name="mfaAuthentication" exclusive>
                  <ToggleButton value {...toggleButtonProps}>
                    <div>Yes</div>
                  </ToggleButton>
                  <ToggleButton value={false} {...toggleButtonProps}>
                    <div>No</div>
                  </ToggleButton>
                </ControlledToggleButtonGroup>
                <FormHelperText className="api-text-error">
                  {_.get(
                    methods,
                    'formState.errors.mfaAuthentication.message',
                    ''
                  )}
                </FormHelperText>
                <QuestionBox show={mfaAuthentication === true}>
                  <InputLabelBase
                    {...LabelProps}
                    style={{ padding: 0, fontStyle: 'italic' }}
                  >
                    (Please select all that apply)
                  </InputLabelBase>
                  <ControlledToggleButtonGroup name="mfaType">
                    <ToggleButton value="mfaEmail" {...toggleButtonProps}>
                      <div>Email</div>
                    </ToggleButton>
                    <ToggleButton
                      value="mfaCloudDeployments"
                      {...toggleButtonProps}
                    >
                      <div>Cloud Deployment</div>
                    </ToggleButton>
                    <ToggleButton
                      value="mfaMissionCriticalSystems"
                      {...toggleButtonProps}
                    >
                      <div>Mission-critical systems</div>
                    </ToggleButton>
                    <ToggleButton
                      value="mfaRemoteAccess"
                      {...toggleButtonProps}
                    >
                      <div>Remote Access</div>
                    </ToggleButton>
                    <ToggleButton value="mfaOther" {...toggleButtonProps}>
                      <div>Other</div>
                    </ToggleButton>
                  </ControlledToggleButtonGroup>
                </QuestionBox>
                <FormHelperText className="api-text-error">
                  {_.get(methods, 'formState.errors.mfaType.message', '')}
                </FormHelperText>
                <QuestionBox
                  show={
                    mfaAuthentication === true &&
                    typeof mfaType === 'object' &&
                    mfaType.includes('mfaOther')
                  }
                >
                  <div>
                    <InputLabelBase
                      {...LabelProps}
                      style={{ padding: 0, fontStyle: 'italic' }}
                    >
                      (Other: Please explain in detail your MFA implementation
                      throughout the organization)
                    </InputLabelBase>
                    <TextArea
                      multiline
                      minRows={3}
                      name="mfaOther"
                      fullWidth
                      className={classes.input}
                    />
                  </div>
                  <FormHelperText className="api-text-error">
                    {_.get(methods, 'formState.errors.mfaOther.message', '')}
                  </FormHelperText>
                </QuestionBox>
              </div>
            </Grid>

            <Grid item md={12}>
              <div>
                <InputLabelBase {...LabelProps}>
                  Does the organization have an incident response plan - tested
                  and in-effect - setting forth specific action items and
                  responsibilities for relevant parties in the event of cyber
                  incident or data breach matter?
                </InputLabelBase>
                <ControlledToggleButtonGroup
                  name="incidentResponsePlan"
                  exclusive
                >
                  <ToggleButton value {...toggleButtonProps}>
                    <div>Yes</div>
                  </ToggleButton>
                  <ToggleButton value={false} {...toggleButtonProps}>
                    <div>No</div>
                  </ToggleButton>
                </ControlledToggleButtonGroup>
                <FormHelperText className="api-text-error">
                  {_.get(
                    methods,
                    'formState.errors.incidentResponsePlan.message',
                    ''
                  )}
                </FormHelperText>
              </div>
            </Grid>

            <QuestionBox item md={12} show={Boolean(isWithinExpiryPeriod)}>
              <div>
                <InputLabelBase {...LabelProps}>
                  In the 12 months prior to this renewal, has the organization
                  experienced or is contemplating material change in the scope
                  of the organization's business, including the mergers,
                  acquisitions,divestitures, and/or repurposing?
                </InputLabelBase>
                <ControlledToggleButtonGroup name="changeInScope" exclusive>
                  <ToggleButton value {...toggleButtonProps}>
                    <div>Yes</div>
                  </ToggleButton>
                  <ToggleButton value={false} {...toggleButtonProps}>
                    <div>No</div>
                  </ToggleButton>
                </ControlledToggleButtonGroup>
                <FormHelperText
                  className="api-text-error"
                  style={{ textAlign: 'start' }}
                >
                  {_.get(methods, 'formState.errors.changeInScope.message', '')}
                </FormHelperText>
                <QuestionBox show={changeInScope === true} item md={12}>
                  <InputLabelBase
                    {...LabelProps}
                    style={{ padding: 0, fontStyle: 'italic' }}
                  >
                    (Please provide details)
                  </InputLabelBase>
                  <TextArea
                    multiline
                    minRows={4}
                    name="changeInScopeDetail"
                    fullWidth
                    className={classes.input}
                  />
                  <FormHelperText className="api-text-error">
                    {_.get(
                      methods,
                      'formState.errors.changeInScopeDetail.message',
                      ''
                    )}
                  </FormHelperText>
                </QuestionBox>
              </div>
            </QuestionBox>
          </QuestionBox>
        </Grid>
      </MuiCard>
    </FormProvider>
  );
};

const styles = ({ palette, config }: any) => {
  return {
    greenLabel: {
      color: config.colors.green5,
      paddingTop: 0,
      marginTop: 0,
    },
    inputFields: {
      width: '95%',
      float: 'left',
      margin: '0 0 0rem 0 !important',
      justifyContent: 'space-evenly',
    },
    input: {
      fontFamily: config.fonts.stackSans,
      fontSize: config.textSizes.normal,
      lineHeight: 1.17,
      width: '100%',
      background: 'inherit',
      border: `solid 0.8px ${palette.primary.contrastText}`,
      borderRadius: 5,
      color: palette.select.disabled,
      padding: '0.416rem',
    },
    text: {
      fontSize: config.textSizes.tertia,
      color: palette.primary.contrastText,
      paddingLeft: (props: any) => {
        if (props.from === 'orgProfile') {
          return '8rem';
        }
        return 0;
      },
      fontWeight: config.weights.bold,
      textTransform: 'uppercase',
      marginTop: '2rem',
    },
    toggleButton: {
      padding: '0.5rem 4rem',
    },
    toggleButtonDisabled: {
      color: `${palette.primary.contrastText} !important`,
    },
  };
};

// @ts-expect-error TS(2345): Argument of type '({ palette, config }: any) => { ... Remove this comment to see the full error message
export default withStyles(styles)(RenewalQuestions);
